import { useEffect, useMemo } from "react";

import * as Cmp from "@impero-com/common/utils/comparison";

import { useFeatureFlag } from "utils/hooks/useFeatureFlag";

import { useDashboardPendingActivitiesQuery } from "../queries/useDashboardPendingActivitiesQuery";
import { usePendingTestingActivitiesQuery } from "../queries/usePendingTestingActivitiesQuery";
import { buildPendingActivities, buildPendingTestRows } from "../tableUtils";
import { PendingActivityTableRow } from "../types";

export type PendingWorkQueueRows =
    | { status: "pending" }
    | { status: "error"; error: unknown }
    | { status: "success"; rows: PendingActivityTableRow[] };

// Ties on the due date fall back to the name, then the id, so the order is stable
const compareRows = Cmp.chain<PendingActivityTableRow>([
    Cmp.map((row) => new Date(row.pendingStepDueDate).getTime()),
    Cmp.map((row) => row.activityName, Cmp.string()),
    Cmp.map((row) => row.id),
]);

// TODO: sc-42913 Replace this client-side merge with one backend endpoint
/**
 * The current user's pending controls and reviews, plus their pending tests when Control Testing
 * is enabled, merged into one list sorted by due date.
 */
export function usePendingWorkQueueRows(): PendingWorkQueueRows {
    const controlTestingEnabled = useFeatureFlag("controlTesting");
    const controls = useDashboardPendingActivitiesQuery({
        includeClaimedByOthers: true,
        limit: 10000,
    });
    const { hasNextPage, isFetching, isError, fetchNextPage } = controls;

    const tests = usePendingTestingActivitiesQuery({
        enabled: controlTestingEnabled,
    });

    useEffect(() => {
        if (hasNextPage && !isFetching && !isError) {
            fetchNextPage();
        }
    }, [hasNextPage, isFetching, isError, fetchNextPage]);

    const rows = useMemo(() => {
        if (controls.data === undefined || hasNextPage) {
            return null;
        }
        if (!controlTestingEnabled) {
            return buildPendingActivities(controls.data).sort(compareRows);
        }
        if (tests.data === undefined) {
            return null;
        }
        return [
            ...buildPendingActivities(controls.data),
            ...buildPendingTestRows(tests.data),
        ].sort(compareRows);
    }, [controls.data, hasNextPage, controlTestingEnabled, tests.data]);

    // Loaded rows win over a failed background refetch, so the list stays on screen
    if (rows !== null) {
        return { status: "success", rows };
    }
    if (controls.isError) {
        return { status: "error", error: controls.error };
    }
    if (controlTestingEnabled && tests.isError) {
        return { status: "error", error: tests.error };
    }
    return { status: "pending" };
}
